import React, { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useProgress } from '../context/ProgressContext';
import { Confetti, ToastContainer } from '../components';
import type { ToastData } from '../components';
import { getLessonById, lessons } from '../data/lessons';
import { getTrophyById } from '../data/trophies';
import './LessonPage.css';

export const LessonPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { progress, completeLesson } = useProgress();

  const [checkedItems, setCheckedItems] = useState<Set<number>>(new Set());
  const [showConfetti, setShowConfetti] = useState(false);
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [activeTab, setActiveTab] = useState<'teorie' | 'construire' | 'recap'>('teorie');

  const lessonId = parseInt(id || '1', 10);
  const lesson = getLessonById(lessonId);
  const trophy = lesson ? getTrophyById(lesson.trofeulId) : null;

  const isCompleted = progress.lessonsCompleted.includes(lessonId);
  const allChecked = lesson ? checkedItems.size === lesson.checklist.length : false;

  const addToast = useCallback((message: string, type: ToastData['type']) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleCheckItem = (index: number) => {
    const newChecked = new Set(checkedItems);
    if (newChecked.has(index)) {
      newChecked.delete(index);
    } else {
      newChecked.add(index);
    }
    setCheckedItems(newChecked);
  };

  const handleComplete = () => {
    if (!lesson || !trophy) return;

    completeLesson(lessonId, lesson.trofeulId);
    setShowConfetti(true);
    addToast(`Ai câștigat: ${trophy.nume}! 🏆`, 'success');
  };

  const handleConfettiComplete = () => {
    setShowConfetti(false);
  };

  if (!lesson) {
    return (
      <div className="lesson-page">
        <div className="lesson-not-found">
          <h1>Lecția nu a fost găsită</h1>
          <Link to="/plan" className="btn btn-primary">
            Înapoi la Plan
          </Link>
        </div>
      </div>
    );
  }

  const prevLesson = lessonId > 1 ? getLessonById(lessonId - 1) : null;
  const nextLesson = lessonId < lessons.length ? getLessonById(lessonId + 1) : null;
  const canAccessNext = nextLesson && progress.lessonsCompleted.includes(lessonId);

  return (
    <div className="lesson-page">
      <Confetti isActive={showConfetti} onComplete={handleConfettiComplete} />
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <header className="lesson-header">
        <Link to="/plan" className="back-link">
          ← Înapoi la Plan
        </Link>
        <div className="lesson-meta">
          <span className="lesson-badge">Ziua {lesson.id}</span>
          <span className="lesson-week">Săptămâna {lesson.saptamana}</span>
          {isCompleted && <span className="lesson-completed-badge">✓ Completată</span>}
        </div>
        <h1 className="lesson-title">{lesson.titlu}</h1>
      </header>

      <div className="lesson-tabs">
        <button
          className={`lesson-tab ${activeTab === 'teorie' ? 'active' : ''}`}
          onClick={() => setActiveTab('teorie')}
          type="button"
        >
          📖 Teorie
          <span className="tab-time">5 min</span>
        </button>
        <button
          className={`lesson-tab ${activeTab === 'construire' ? 'active' : ''}`}
          onClick={() => setActiveTab('construire')}
          type="button"
        >
          🔨 Construire
          <span className="tab-time">20 min</span>
        </button>
        <button
          className={`lesson-tab ${activeTab === 'recap' ? 'active' : ''}`}
          onClick={() => setActiveTab('recap')}
          type="button"
        >
          ✓ Recap
          <span className="tab-time">5 min</span>
        </button>
      </div>

      <div className="lesson-content">
        {activeTab === 'teorie' && (
          <section className="lesson-section">
            <h2>Teorie</h2>
            <div className="theory-content">
              {lesson.teorie.split('\n\n').map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setActiveTab('construire')}
              type="button"
            >
              Continuă cu Construirea →
            </button>
          </section>
        )}

        {activeTab === 'construire' && (
          <section className="lesson-section">
            <h2>Pași de Construire</h2>
            <ol className="build-steps">
              {lesson.pasiConstruire.map((step, i) => (
                <li key={i} className="build-step">
                  {step}
                </li>
              ))}
            </ol>
            <button
              className="btn btn-primary"
              onClick={() => setActiveTab('recap')}
              type="button"
            >
              Continuă cu Recap →
            </button>
          </section>
        )}

        {activeTab === 'recap' && (
          <section className="lesson-section">
            <h2>Checklist Final</h2>
            <p className="checklist-intro">
              Bifează toate itemurile pentru a finaliza lecția și a debloca trofeul.
            </p>
            <div className="checklist">
              {lesson.checklist.map((item, i) => (
                <label key={i} className="checklist-item">
                  <input
                    type="checkbox"
                    checked={checkedItems.has(i)}
                    onChange={() => handleCheckItem(i)}
                    disabled={isCompleted}
                  />
                  <span className="checkmark"></span>
                  <span className="checklist-text">{item}</span>
                </label>
              ))}
            </div>

            {trophy && (
              <div className="trophy-preview">
                <h3>Trofeu de Deblocat</h3>
                <div className="trophy-preview-card">
                  <div
                    className="trophy-preview-icon"
                    dangerouslySetInnerHTML={{ __html: trophy.icon }}
                  />
                  <div className="trophy-preview-info">
                    <span className="trophy-preview-name">{trophy.nume}</span>
                    <span className="trophy-preview-desc">{trophy.descriere}</span>
                  </div>
                </div>
              </div>
            )}

            {!isCompleted && (
              <button
                className="btn btn-primary btn-lg complete-btn"
                onClick={handleComplete}
                disabled={!allChecked}
                type="button"
              >
                {allChecked ? 'Marchează ca Făcut ✓' : `Bifează toate itemurile (${checkedItems.size}/${lesson.checklist.length})`}
              </button>
            )}

            {isCompleted && (
              <div className="completed-message">
                <span className="completed-icon">🎉</span>
                <span>Ai completat această lecție!</span>
              </div>
            )}
          </section>
        )}
      </div>

      <nav className="lesson-nav">
        {prevLesson ? (
          <Link to={`/lectie/${prevLesson.id}`} className="lesson-nav-link prev">
            <span className="nav-arrow">←</span>
            <div className="nav-info">
              <span className="nav-label">Lecția anterioară</span>
              <span className="nav-title">Ziua {prevLesson.id}</span>
            </div>
          </Link>
        ) : (
          <div />
        )}

        {nextLesson && (
          <Link
            to={canAccessNext ? `/lectie/${nextLesson.id}` : '#'}
            className={`lesson-nav-link next ${!canAccessNext ? 'disabled' : ''}`}
            onClick={(e) => !canAccessNext && e.preventDefault()}
          >
            <div className="nav-info">
              <span className="nav-label">Lecția următoare</span>
              <span className="nav-title">Ziua {nextLesson.id}</span>
            </div>
            <span className="nav-arrow">→</span>
          </Link>
        )}
      </nav>
    </div>
  );
};
